/**
 * Shared formatting helpers used across the schedule views.
 *
 * Day ordering: enforce Mon → Sun regardless of click order.
 * Time formatting: UI shows 12-hour AM/PM; internally we still store HH:MM 24h.
 */

export const DAY_ORDER = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** Sort an array of day codes into canonical weekly order. */
export function sortDays(days) {
  if (!Array.isArray(days)) return [];
  const rank = Object.fromEntries(DAY_ORDER.map((d, i) => [d, i]));
  return days
    .filter((d) => rank[d] !== undefined)
    .slice()
    .sort((a, b) => rank[a] - rank[b]);
}

/** Convert an internal "HH:MM" 24-hour string to "h:MM AM/PM". Falls back to "—" for null/undefined. */
export function formatTime12(hhmm) {
  if (!hhmm || typeof hhmm !== "string" || hhmm.length < 4) return hhmm ? hhmm : "—";
  const [hStr, mStr = "00"] = hhmm.split(":");
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10) || 0;
  if (Number.isNaN(h)) return hhmm;
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${period}`;
}

/** Convert a "HH:MM-HH:MM" 24-hour window string to "h:MM–h:MM AM/PM" 12-hour.
 *  Example: "07:00-07:15" → "7:00–7:15 AM". Also accepts "H:MM-H:MM" and
 *  drops the trailing AM/PM on the start when both ends share the same
 *  period, e.g., "12:45-13:00" → "12:45 PM – 1:00 PM" (both PM ⇒ compact).
 */
export function formatTimeWindow12(range) {
  if (!range || typeof range !== "string") return "—";
  const [start, end] = range.split("-");
  if (!start || !end) return formatTime12(range);
  const s = formatTime12(start);
  const e = formatTime12(end);
  const sp = s.slice(-2);
  const ep = e.slice(-2);
  if (sp === ep) {
    // Same period — drop the AM/PM on the start for a compact range.
    return `${s.replace(/ (AM|PM)$/, "")}–${e}`;
  }
  return `${s} – ${e}`;
}

/** Render a 15-minute transportation window starting at the given HH:MM time.
 *  Example: formatPickupWindow("14:15") → "2:15 PM – 2:30 PM"
 *  Used by /onboarding, /waitlist, /waitlist/status, and the live /schedule
 *  booking flow so every rider-facing time surface speaks "window" language.
 */
export function formatPickupWindow(hhmm) {
  if (!hhmm || typeof hhmm !== "string" || hhmm.length < 4) return formatTime12(hhmm);
  const [hStr, mStr = "00"] = hhmm.split(":");
  let h = parseInt(hStr, 10);
  let m = parseInt(mStr, 10) || 0;
  if (Number.isNaN(h)) return hhmm;
  // Round down to the nearest 15-minute boundary so a 8:07 pickup
  // surfaces as the 8:00 – 8:15 window.
  m = Math.floor(m / 15) * 15;
  let endH = h;
  let endM = m + 15;
  if (endM === 60) { endM = 0; endH = (h + 1) % 24; }
  const pad = (n) => String(n).padStart(2, "0");
  return `${formatTime12(`${pad(h)}:${pad(m)}`)} – ${formatTime12(`${pad(endH)}:${pad(endM)}`)}`;
}


// ---------------------------------------------------------------------------
// Explicit-date formatters (Phase A tail #2)
// ---------------------------------------------------------------------------
//
// Never render bare day names ("Monday", "Wed") when a real calendar date
// is available. A rider who opens the dashboard on a Wednesday should see
// "Wed, Jul 22 · 2:15 – 2:30 PM", not "Wednesday · 2:15 PM" — the former
// removes an entire class of "which Monday?" support tickets and makes
// receipts self-describing when forwarded to insurance / HR.
//
// Every date input is normalised through `_toDate` so we accept both:
//   • ISO date strings (`"2026-07-22"`, `"2026-07-22T14:15:00Z"`)
//   • JS Date objects
// Invalid / missing inputs render as `"—"` (not "Invalid Date").

function _toDate(input) {
  if (!input) return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  if (typeof input === "string") {
    // "YYYY-MM-DD" (bare date) is parsed as UTC-midnight by the Date
    // constructor, which shifts the wall-clock day westward in the US.
    // Force local-noon so the rendered weekday matches what the rider sees.
    if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
      const [y, mo, d] = input.split("-").map(Number);
      const dt = new Date(y, mo - 1, d, 12, 0, 0, 0);
      return isNaN(dt.getTime()) ? null : dt;
    }
    const dt = new Date(input);
    return isNaN(dt.getTime()) ? null : dt;
  }
  return null;
}

/**
 * Explicit weekday+month+day. Used everywhere a rider needs to identify a
 * SPECIFIC calendar day, not the abstract weekday.
 *
 * `formatExplicitDate("2026-07-22")` → `"Wed, Jul 22"`
 * `formatExplicitDate("2026-07-22", { withYear: true })` → `"Wed, Jul 22, 2026"`
 * `formatExplicitDate(null)` → `"—"`
 */
export function formatExplicitDate(input, opts = {}) {
  const dt = _toDate(input);
  if (!dt) return "—";
  const withYear = opts.withYear === true;
  return dt.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(withYear ? { year: "numeric" } : {}),
  });
}

/**
 * Explicit date + time window in one string, comma-separated. This is the
 * canonical rider-facing "when" for a single trip.
 *
 * `formatExplicitDateTime("2026-07-22", "14:15")` → `"Wed, Jul 22 · 2:15 – 2:30 PM"`
 * Falls back to date-only or time-only when the other half is missing.
 */
export function formatExplicitDateTime(dateInput, hhmm) {
  const datePart = dateInput ? formatExplicitDate(dateInput) : "";
  const timePart = hhmm ? formatPickupWindow(hhmm) : "";
  if (datePart && timePart) return `${datePart} · ${timePart}`;
  return datePart || timePart || "—";
}

/**
 * Relative label with an explicit date always present as the trailing
 * qualifier. Riders skim "Today", "Tomorrow", "In 3 days" for the primary
 * signal but the trailing date protects against off-by-one panics.
 *
 * `formatRelativeDate("2026-07-22")` (rendered on 2026-07-22) → `"Today · Wed, Jul 22"`
 * `formatRelativeDate("2026-07-23")` (rendered on 2026-07-22) → `"Tomorrow · Thu, Jul 23"`
 * `formatRelativeDate("2026-07-25")` (rendered on 2026-07-22) → `"Sat, Jul 25"`  (no relative label past 1 day)
 */
export function formatRelativeDate(input) {
  const dt = _toDate(input);
  if (!dt) return "—";
  const now = new Date();
  const startOf = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startOf(dt) - startOf(now)) / 86400000);
  const explicit = formatExplicitDate(input);
  if (diffDays === 0) return `Today · ${explicit}`;
  if (diffDays === 1) return `Tomorrow · ${explicit}`;
  if (diffDays === -1) return `Yesterday · ${explicit}`;
  return explicit;
}

